<template>
  <div class="quick-view-confirmation" v-data-test-id="'quick-view-confirmation'">
    <div class="quick-view-confirmation__title">{{ $t('checkout.cart.added_to_basket') }}</div>
    <div class="quick-view-confirmation__image-container">
      <ImageContentful
        class="quick-view-confirmation__image"
        :src="variant.defaultImage.mediaUrl"
        :options="{
          imageCompression: 81,
          setHeight: false,
          fixedWidth: 180,
          retinaSupportFactor: 0.2,
          format: 'jpg',
          backgroundColor: 'f7f7f7',
        }"
      />
    </div>
    <div class="quick-view-confirmation__description-box">
      <div class="quick-view-confirmation__title-box">
        <div class="quick-view-confirmation__product-title">{{ productName }}</div>
        <div class="quick-view-confirmation__price">
          {{ priceWithCurrency }}
        </div>
      </div>
      <div class="quick-view-confirmation__color">
        {{ variant.color }}
      </div>
      <div class="quick-view-confirmation__size">
        <span class="quick-view-confirmation__size-system">{{ sizeSystem }} </span>
        <span>{{ $t(`pdp.purchase_pod.${variant.gender}`) }}</span>
        <span>{{ selectedSkuObj.size }}</span>
        {{ variant.size }}
      </div>
    </div>
    <div class="quick-view-confirmation__button-box">
      <Button
        class="quick-view-confirmation__button"
        :href="getFullUrl(CHECKOUT_PATH)"
        tag="a"
        :fontSize="BUTTON_STYLES.fontSize.small"
        :height="BUTTON_STYLES.height.small"
        :isSubmitting="isSubmitting"
        @click="goToCheckout"
        >{{ $t('filter.quickview.proceed_checkout') }}</Button
      >
      <Button
        class="quick-view-confirmation__button"
        :color="BUTTON_STYLES.color.white"
        :fontSize="BUTTON_STYLES.fontSize.small"
        :height="BUTTON_STYLES.height.small"
        @click="hide"
        >{{ $t('filter.quickview.continue_shopping') }}</Button
      >
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import { CHECKOUT_PATH } from '@/config/constants'
import { formatToCurrency } from '@/helpers/currency-formatter'
import Button, { BUTTON_STYLES } from '@/components/_shared/Button.vue'
import { STATES } from '@/components/_shared/Cards/Card/QuickView/QuickView.vue'
import ImageContentful from '@/components/_shared/ImageContentful.vue'

export default {
  name: 'QuickViewConfirmation',
  components: { Button, ImageContentful },
  props: {
    productName: {
      type: String,
      required: true,
    },
    selectedSkuObj: {
      type: Object,
      required: true,
    },
    variant: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      BUTTON_STYLES,
      CHECKOUT_PATH,
      isSubmitting: false,
    }
  },

  computed: {
    ...mapState({
      currency: state => state.store.currency,
      locale: state => state.page.locale,
    }),
    ...mapGetters({ getFullUrl: 'page/getFullUrl' }),

    priceWithCurrency() {
      const amount = Number(this.variant.spreeProduct.price)
      return formatToCurrency(amount, this.locale, this.currency)
    },
  },

  watch: {
    isSubmitting: {
      handler: function () {
        this.$notifier.trackEvent({
          action: this.$notifier.ACTIONS.productInteraction,
          details: { action: 'quickview_3', label: 'proceed_to_checkout' },
        })
      },
    },
  },

  methods: {
    ...mapActions({
      hideOverlay: 'overlay/hide',
    }),

    hide() {
      this.hideOverlay('quick-view')
      this.$notifier.trackEvent({
        action: this.$notifier.ACTIONS.productInteraction,
        details: { action: 'quickview_3', label: 'continue_shopping' },
      })
    },

    goToCheckout() {
      this.isSubmitting = true
      this.$notifier.trackEvent({
        action: this.$notifier.ACTIONS.productInteraction,
        details: { action: 'quickview_3', label: 'proceed_to_checkout' },
      })
    },

    triggerStateChange() {
      this.$emit('state-change', STATES.overview)
    },
  },
}
</script>

<style lang="scss" scoped>
.quick-view-confirmation {
  position: relative;
  display: flex;
  flex: 1;
  margin-top: 70px;
  flex-direction: column;
  padding: 20px 20px 20px;
  font-family: $font-family-helvetica-now;

  &__inner {
    flex: 1;
    display: flex;
    flex-direction: column;
  }

  &__title {
    font-size: 18px;
    color: $color-grey-dark-3;
    flex: 1;
    display: flex;
    flex-direction: column;
    text-align: center;
    margin-bottom: 20px;
  }

  &__image-container {
    width: 100%;
    height: 180px;
    background-color: $color-grey-light-4;
  }

  &__image {
    width: 180px;
  }

  &__description-box {
    flex: 1;
    padding: 10px 0 20px;
    margin-bottom: 10px;
  }

  &__title-box {
    display: flex;
  }

  &__product-title {
    flex: 1;
    font-weight: bold;
    color: $color-black;
    text-align: left;
  }

  &__price {
    position: relative;
    top: 2px;
    font-size: $f-size-smaller;
    color: $c-grey-dark-2;
  }

  &__color,
  &__size {
    font-size: $font-size-smaller;
    color: $c-grey-mid-1;
    text-align: left;
    line-height: 1.4;
  }

  &__size-system {
    text-transform: uppercase;
  }

  &__button {
    margin-top: 10px;
  }
}
</style>
